/**
 * ModeToggle — switches between keyword, semantic, and agentic ("Ask") search.
 *
 * Rendered as an accessible toggle-button group (role="group", each option a
 * toggle button exposing `aria-pressed`). Selection is fully keyboard
 * reachable; the active mode is owned by the URL via the `onChange` callback.
 */
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { SearchMode } from "./urlState";
import type { ReactElement } from "react";

const OPTIONS: readonly { mode: SearchMode; label: string }[] = [
  { mode: "keyword", label: "Keyword" },
  { mode: "semantic", label: "Semantic" },
  { mode: "ask", label: "Ask" },
];

export interface ModeToggleProps {
  mode: SearchMode;
  onChange: (mode: SearchMode) => void;
}

export function ModeToggle({ mode, onChange }: ModeToggleProps): ReactElement {
  return (
    <ToggleButtonGroup
      value={mode}
      exclusive
      color="primary"
      aria-label="Search mode"
      onChange={(_event, next: SearchMode | null) => {
        if (next) onChange(next);
      }}
    >
      {OPTIONS.map((option) => (
        <ToggleButton key={option.mode} value={option.mode} aria-label={option.label}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
